/**
 * Contact Form Page with CAPTCHA Integration
 *
 * This module implements a contact form with dual CAPTCHA protection using both
 * HCaptcha and Turnstile (Cloudflare). It demonstrates how to integrate multiple
 * CAPTCHA providers for enhanced protection against automated submissions.
 *
 * The form includes:
 * - Basic contact information fields (name, email, message)
 * - Server-side validation using Zod schemas
 * - CAPTCHA verification with both HCaptcha and Turnstile
 * - Email sending via Resend API
 * - Form state management and user feedback
 *
 * This implementation serves as a demonstration of how to implement robust
 * form protection and validation in a production application.
 */
import type { Route } from "./+types/contact-us";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, data } from "react-router";
import Turnstile, { useTurnstile } from "react-turnstile";
import { toast } from "sonner";
import { z } from "zod";

import FormButton from "~/core/components/form-button";
import FormErrors from "~/core/components/form-error";
import { Input } from "~/core/components/ui/input";
import { Label } from "~/core/components/ui/label";
import { Textarea } from "~/core/components/ui/textarea";
import resendClient from "~/core/lib/resend-client.server";

/**
 * Meta function for setting page metadata
 *
 * This function sets the page title for the Contact Us page,
 * using the application name from environment variables.
 *
 * @returns Array of metadata objects for the page
 */
export const meta: Route.MetaFunction = () => {
  return [
    {
      title: `Contact Us | ${import.meta.env.VITE_APP_NAME}`,
    },
  ];
};

/**
 * Validates a Turnstile CAPTCHA token with Cloudflare's API
 *
 * This function sends the token received from the client-side Turnstile widget
 * to Cloudflare's verification endpoint to confirm that the user successfully
 * completed the CAPTCHA challenge.
 *
 * The verification process:
 * 1. Sends the token and secret key to Cloudflare's verification endpoint
 * 2. Parses the JSON response to determine if the token is valid
 * 3. Returns a boolean indicating success or failure
 * 4. Handles errors gracefully, logging them and returning false
 *
 * @param token - The token received from the client-side Turnstile widget
 * @returns Promise resolving to a boolean indicating if the token is valid
 */
async function isTurnstileTokenValid(token: string) {
  try {
    // Cloudflare's verification endpoint
    const url = "https://challenges.cloudflare.com/turnstile/v0/siteverify";

    // Send verification request to Cloudflare
    const result = await fetch(url, {
      body: JSON.stringify({
        secret: process.env.TURNSTILE_SECRET_KEY,
        response: token,
      }),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Parse response and return success status
    const outcome = await result.json();
    return outcome.success;
  } catch (error) {
    // Log error and return false on failure
    console.error(error);
    return false;
  }
}

/**
 * Validation schema for contact form submissions
 *
 * This schema defines the required fields and validation rules for the contact form:
 * - name: Required, must be at least 1 character
 * - email: Required, must be a valid email format
 * - message: Required, must be at least 1 character
 * - hcaptcha: Required, must contain a valid HCaptcha token
 * - turnstile: Required, must contain a valid Turnstile token
 *
 * The schema is used with Zod's safeParse method to validate form submissions
 * before processing them further.
 */
const schema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  message: z.string().min(1),
  turnstile: z.string().min(1),
});

/**
 * Action handler for processing contact form submissions
 *
 * This function processes form submissions from the contact page. It follows these steps:
 * 1. Extracts and validates form data using the Zod schema
 * 2. Verifies both CAPTCHA tokens with their respective services
 * 3. Sends an email to the admin with the contact information
 * 4. Returns appropriate success or error responses
 *
 * Security considerations:
 * - Validates all form fields to prevent invalid data
 * - Verifies CAPTCHA tokens to prevent spam and automated submissions
 * - Uses server-side validation to prevent client-side bypass
 * - Handles errors gracefully with appropriate status codes
 *
 * @param request - The incoming HTTP request with form data
 * @returns JSON response indicating success or error with appropriate details
 */
export async function action({ request }: Route.ActionArgs) {
  console.log("=== CONTACT FORM ACTION STARTED ===");

  try {
    // Extract form data from the request
    const formData = await request.formData();
    console.log("Form data received:", Object.fromEntries(formData));

    // Validate form data using the Zod schema
    const result = schema.safeParse(Object.fromEntries(formData));
    if (!result.success) {
      console.error("Form validation failed:", result.error);
      // Return validation errors if the form data is invalid
      return data(
        { fieldErrors: result.error.flatten().fieldErrors, success: false },
        { status: 400 },
      );
    }

    // Extract validated data
    const { name, email, message, turnstile } = result.data;
    console.log("Parsed form data:", {
      name,
      email,
      message: message.substring(0, 50) + "...",
    });

    // Verify Turnstile token
    const validTurnstile = await isTurnstileTokenValid(turnstile);

    // Return error if CAPTCHA verification fails
    if (!validTurnstile) {
      return data(
        {
          errors: {
            turnstile: ["Invalid captcha, please try again"],
          },
          success: false,
        },
        { status: 400 },
      );
    }

    // Send email to admin with contact information
    console.log("=== EMAIL SENDING SECTION STARTED ===");

    // Debug: Log the data being sent
    console.log("Sending email with data:", { name, email, message });
    console.log("Admin email:", process.env.ADMIN_EMAIL);
    console.log("Resend API Key exists:", !!process.env.RESEND_API_KEY);
    console.log("Resend API Key length:", process.env.RESEND_API_KEY?.length);
    console.log(
      "Resend API Key first 10 chars:",
      process.env.RESEND_API_KEY?.substring(0, 10),
    );

    // Check if required environment variables are set
    if (!process.env.RESEND_API_KEY) {
      throw new Error("RESEND_API_KEY environment variable is not set");
    }

    if (!process.env.ADMIN_EMAIL) {
      throw new Error("ADMIN_EMAIL environment variable is not set");
    }

    const emailHtml = `
      <h2>새로운 문의가 도착했습니다</h2>
      <p><b>이름:</b> ${name}</p>
      <p><b>이메일:</b> ${email}</p>
      <p><b>메시지:</b> ${message}</p>
      <hr>
      <p><small>AI내동생 Contact Form에서 전송됨</small></p>
    `;

    console.log("Email HTML content:", emailHtml);

    // Test with simpler email format first
    // Test with minimal email configuration
    console.log("About to send email with Resend...");
    console.log("Resend client:", resendClient);

    console.log("=== ABOUT TO CALL RESEND API ===");

    // Send email using verified domain (when ready) or test domain (for testing)
    const { error } = await resendClient.emails.send({
      from: "AI내동생 <noreply@aimybro.com>", // Use your verified domain
      to: [process.env.ADMIN_EMAIL!], // Send to admin email
      subject: "새로운 문의가 도착했습니다",
      html: emailHtml,
    });

    console.log("=== RESEND API CALL COMPLETED ===");
    console.log("Resend API response:", { error });

    // Handle email sending errors
    if (error) {
      console.error("Resend API Error:", error);
      return data(
        {
          error: {
            message: `이메일 전송 실패: ${error.message || "알 수 없는 오류"}`,
          },
          success: false,
        },
        { status: 500 },
      );
    }

    console.log("Email sent successfully!");
  } catch (err) {
    console.error("Email sending failed:", err);
    console.error("Error type:", typeof err);
    console.error("Error constructor:", err?.constructor?.name);
    console.error(
      "Error stack:",
      err instanceof Error ? err.stack : "No stack trace",
    );

    const errorMessage = err instanceof Error ? err.message : String(err);
    return data(
      {
        error: { message: `이메일 전송에 실패했습니다: ${errorMessage}` },
        success: false,
      },
      { status: 500 },
    );
  }

  // Return success response
  return data({
    success: true,
    error: null,
  });
}

/**
 * Contact Us Form Component
 *
 * This component renders a contact form with dual CAPTCHA protection.
 * It manages form state, CAPTCHA tokens, and provides user feedback
 * based on the form submission results.
 *
 * @param actionData - Data returned from the action function after form submission
 */
export default function ContactUs({ actionData }: Route.ComponentProps) {
  // Get translation function for the current locale
  const { t } = useTranslation();

  // State for storing Turnstile token
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  // State to control when to render CAPTCHA widget (prevents SSR issues)
  const [renderCaptcha, setRenderCaptcha] = useState<boolean>(false);

  // References to interact with CAPTCHA widget and form
  const turnstile = useTurnstile(); // Hook for Turnstile widget interactions
  const formRef = useRef<HTMLFormElement>(null); // Reference to the form element

  // Debug environment variables
  useEffect(() => {
    console.log("Turnstile Site Key:", import.meta.env.VITE_TURNSTILE_SITE_KEY);
    console.log(
      "Turnstile Site Key length:",
      import.meta.env.VITE_TURNSTILE_SITE_KEY?.length,
    );
  }, []);

  /**
   * Effect for handling form submission results
   *
   * This effect runs whenever actionData changes (after form submission).
   * It handles:
   * 1. Resetting both CAPTCHA widgets
   * 2. Clearing CAPTCHA tokens
   * 3. Showing success or error messages
   * 4. Resetting the form on successful submission
   */
  useEffect(() => {
    if (!actionData) return;

    // Reset Turnstile widget and token
    turnstile.reset();
    setTurnstileToken("");

    // Handle successful submission
    if (actionData?.success) {
      // Show success message
      toast.success(t("contact.messages.success"));

      // Reset form and remove focus from inputs
      formRef.current?.reset();
      formRef.current?.querySelectorAll("input").forEach((input) => {
        input.blur();
      });
    }
    // Handle error in submission
    else if ("error" in actionData && actionData.error) {
      const errorMessage =
        actionData.error.message || t("contact.messages.error");
      console.error("Form submission error:", actionData.error);
      toast.error(errorMessage);
    }
  }, [actionData]);

  /**
   * Effect for delayed rendering of CAPTCHA widget
   *
   * This effect runs once on component mount and enables CAPTCHA rendering.
   * The delayed rendering prevents hydration mismatches and other SSR issues
   * that can occur with third-party CAPTCHA widgets.
   */
  useEffect(() => {
    setRenderCaptcha(true);
  }, []);
  /**
   * Render the contact form with dual CAPTCHA protection
   *
   * The component renders:
   * 1. A header section with title and description
   * 2. A form with name, email, and message fields
   * 3. Two CAPTCHA widgets (HCaptcha and Turnstile)
   * 4. A submit button that is disabled until both CAPTCHAs are verified
   * 5. Error messages for field validation and CAPTCHA verification
   */
  return (
    <div className="flex flex-col items-center gap-20">
      {/* Header section */}
      <div>
        <h1 className="text-center text-3xl font-semibold tracking-tight md:text-5xl">
          {t("contact.title")}
        </h1>
        <p className="text-muted-foreground mt-2 text-center font-medium md:text-lg">
          {t("contact.subtitle")}
        </p>
      </div>

      {/* Contact form */}
      <Form
        method="post"
        ref={formRef}
        className="flex w-full max-w-2xl flex-col gap-5"
      >
        {/* Name field */}
        <div className="flex flex-col items-start space-y-2">
          <Label htmlFor="name" className="flex flex-col items-start gap-1">
            {t("contact.form.name")}
          </Label>
          <Input
            id="name"
            name="name"
            required
            type="text"
            placeholder={t("contact.form.namePlaceholder")}
          />
          {/* Display name field validation errors if any */}
          {actionData &&
          "fieldErrors" in actionData &&
          actionData.fieldErrors?.name ? (
            <FormErrors errors={actionData.fieldErrors.name} />
          ) : null}
        </div>

        {/* Email field */}
        <div className="flex flex-col items-start space-y-2">
          <Label htmlFor="email" className="flex flex-col items-start gap-1">
            {t("contact.form.email")}
          </Label>
          <Input
            id="email"
            name="email"
            required
            type="email"
            placeholder={t("contact.form.emailPlaceholder")}
          />
          {/* Display email field validation errors if any */}
          {actionData &&
          "fieldErrors" in actionData &&
          actionData.fieldErrors?.email ? (
            <FormErrors errors={actionData.fieldErrors.email} />
          ) : null}
        </div>

        {/* Message field */}
        <div className="flex flex-col items-start space-y-2">
          <Label htmlFor="message" className="flex flex-col items-start gap-1">
            {t("contact.form.message")}
          </Label>
          <Textarea
            id="message"
            name="message"
            required
            placeholder={t("contact.form.messagePlaceholder")}
            className="h-32 resize-none"
          />
          {/* Display message field validation errors if any */}
          {actionData &&
          "fieldErrors" in actionData &&
          actionData.fieldErrors?.message ? (
            <FormErrors errors={actionData.fieldErrors.message} />
          ) : null}
        </div>

        {/* Hidden field for Turnstile token */}
        <input type="hidden" name="turnstile" value={turnstileToken} required />

        {/* CAPTCHA widgets - only rendered after initial mount to prevent SSR issues */}
        {/* Turnstile CAPTCHA widget */}
        {renderCaptcha ? (
          <div className="flex justify-center">
            <div>
              <Turnstile
                sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                onVerify={(token) => {
                  setTurnstileToken(token);
                }}
                onError={(error) => {
                  console.error("Turnstile error:", error);
                }}
                onExpire={() => {
                  setTurnstileToken("");
                }}
              />
              {/* Display Turnstile verification errors if any */}
              {actionData &&
              "errors" in actionData &&
              actionData.errors?.turnstile ? (
                <FormErrors
                  key="turnstile"
                  errors={actionData.errors.turnstile}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Informational note about Turnstile CAPTCHA */}
        <span className="text-center text-sm text-blue-500">
          {t("contact.form.captchaNote")}
        </span>

        {/* Submit button - disabled until both CAPTCHAs are verified */}
        <FormButton
          type="submit"
          className="w-full"
          disabled={!turnstileToken}
          label={t("contact.form.sendButton")}
        />
      </Form>
    </div>
  );
}
